import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const Terms = () => {
  const { currentTheme } = useTheme();
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  
  // Scroll to section function - для страниц Privacy и Terms будет перенаправлять на главную
  const scrollToSection = (sectionId) => {
    // Перенаправление на главную страницу с хэшем
    navigate(`/#${sectionId}`);
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  // Content based on language
  const content = {
    ru: {
      title: 'Условия использования',
      lastUpdated: 'Последнее обновление:',
      date: '29 апреля 2025 г.',
      introduction: 'Пожалуйста, внимательно прочитайте эти условия использования перед использованием веб-сайта MoviBobby.',
      sections: [
        {
          title: '1. Принятие условий',
          content: 'Используя наш веб-сайт, вы соглашаетесь с настоящими условиями использования. Если вы не согласны с какой-либо частью этих условий, вы не можете использовать наш сайт.'
        },
        {
          title: '2. Услуги по разработке сайтов',
          content: 'MoviBobby предоставляет услуги по разработке веб-сайтов. Конкретные условия предоставления услуг, включая сроки, стоимость и объем работ, оговариваются в отдельных соглашениях с клиентами.'
        },
        {
          title: '3. Интеллектуальная собственность',
          content: 'Все материалы на этом сайте, включая, но не ограничиваясь текстом, изображениями, кодом, дизайном и логотипами, являются собственностью MoviBobby и защищены законами об авторском праве и интеллектуальной собственности.'
        },
        {
          title: '4. Ограничение ответственности',
          content: 'MoviBobby не несет ответственности за любые прямые, косвенные, случайные, особые или последующие убытки, возникающие в результате использования или невозможности использования наших услуг или материалов на нашем сайте.'
        },
        {
          title: '5. Точность информации',
          content: 'Мы стремимся обеспечить точность и актуальность информации на нашем сайте, но не гарантируем, что содержание является исчерпывающим, точным или актуальным. Мы оставляем за собой право вносить изменения в содержание сайта в любое время без предварительного уведомления.'
        },
        {
          title: '6. Сторонние ссылки',
          content: 'Наш сайт может содержать ссылки на сторонние веб-сайты. Мы не контролируем и не несем ответственности за содержание или политику конфиденциальности этих сайтов.'
        },
        {
          title: '7. Изменения условий',
          content: 'Мы оставляем за собой право изменять эти условия использования в любое время. Продолжая использовать наш сайт после внесения изменений, вы принимаете новые условия.'
        },
        {
          title: '8. Применимое право',
          content: 'Настоящие условия регулируются и толкуются в соответствии с законодательством Российской Федерации.'
        }
      ],
      back: 'Вернуться на главную',
      privacy: 'Политика конфиденциальности'
    },
    en: {
      title: 'Terms of Service',
      lastUpdated: 'Last updated:',
      date: 'April 29, 2025',
      introduction: 'Please read these terms of service carefully before using the MoviBobby website.',
      sections: [
        {
          title: '1. Acceptance of Terms',
          content: 'By using our website, you agree to these terms of service. If you disagree with any part of these terms, you may not use our site.'
        },
        {
          title: '2. Website Development Services',
          content: 'MoviBobby provides website development services. Specific terms of service provision, including timeframes, cost, and scope of work, are stipulated in separate agreements with clients.'
        },
        {
          title: '3. Intellectual Property',
          content: 'All materials on this site, including but not limited to text, images, code, design, and logos, are the property of MoviBobby and are protected by copyright and intellectual property laws.'
        },
        {
          title: '4. Limitation of Liability',
          content: 'MoviBobby shall not be liable for any direct, indirect, incidental, special, or consequential damages resulting from the use or inability to use our services or materials on our website.'
        },
        {
          title: '5. Information Accuracy',
          content: 'We strive to ensure the accuracy and currentness of information on our website but do not guarantee that the content is comprehensive, accurate, or up-to-date. We reserve the right to make changes to the website content at any time without notice.'
        },
        {
          title: '6. Third-party Links',
          content: 'Our website may contain links to third-party websites. We do not control and are not responsible for the content or privacy policies of these sites.'
        },
        {
          title: '7. Changes to Terms',
          content: 'We reserve the right to modify these terms of service at any time. By continuing to use our site after changes are made, you accept the new terms.'
        },
        {
          title: '8. Governing Law',
          content: 'These terms are governed by and construed in accordance with the laws of the Russian Federation.'
        }
      ],
      back: 'Back to home',
      privacy: 'Privacy Policy'
    }
  };

  // Get current language content
  const currentContent = content[currentLanguage] || content.ru;

  return (
    <div className={`${currentTheme === 'light' ? 'bg-light-background text-light-text' : 'bg-[#050505] text-white'} min-h-screen`}>
      {/* Navbar */}
      <Navbar activeSection="" scrollToSection={scrollToSection} />
      
      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mb-12 text-center"
          >
            <h1 className={`text-4xl font-bold mb-4 ${
              currentTheme === 'light' 
                ? 'bg-clip-text text-transparent bg-gradient-to-r from-light-primary to-light-secondary'
                : 'bg-clip-text text-transparent bg-gradient-to-r from-dark-primary to-dark-secondary'
            }`}>
              {currentContent.title}
            </h1>
            <p className={`${currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>
              {currentContent.lastUpdated} {currentContent.date}
            </p>
          </motion.div>
          
          <div className={`max-w-4xl mx-auto ${
            currentTheme === 'light' 
              ? 'bg-white shadow-lg' 
              : 'bg-[#0A0A0A] border border-gray-800'
          } rounded-xl p-8`}>
            <motion.p 
              variants={fadeInUp}
              className={`text-lg mb-8 ${
                currentTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
              }`}
            >
              {currentContent.introduction}
            </motion.p>
            
            {currentContent.sections.map((section, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="mb-8"
              >
                <h2 className={`text-xl font-bold mb-4 ${
                  currentTheme === 'light' ? 'text-gray-800' : 'text-white'
                }`}>
                  {section.title}
                </h2>
                <div 
                  className={`${
                    currentTheme === 'light' ? 'text-gray-700' : 'text-gray-300'
                  } whitespace-pre-line`}
                >
                  {section.content}
                </div>
              </motion.div>
            ))}
            
            <div className="flex flex-col sm:flex-row justify-between items-center mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
              <Link 
                to="/"
                className={`mb-4 sm:mb-0 px-6 py-2 rounded-full ${
                  currentTheme === 'light'
                    ? 'bg-light-primary/10 text-light-primary hover:bg-light-primary/20'
                    : 'bg-dark-primary/10 text-dark-primary hover:bg-dark-primary/20'
                } transition-all duration-300`}
              >
                ← {currentContent.back}
              </Link>
              
              <Link 
                to="/privacy"
                className={`px-6 py-2 rounded-full ${
                  currentTheme === 'light'
                    ? 'bg-light-secondary/10 text-light-secondary hover:bg-light-secondary/20'
                    : 'bg-dark-secondary/10 text-dark-secondary hover:bg-dark-secondary/20'
                } transition-all duration-300`}
              >
                {currentContent.privacy} →
              </Link>
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Terms; 